import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private readonly platformId = inject(PLATFORM_ID);

  readonly isBrowser = isPlatformBrowser(this.platformId);

  add(name: string, data: any) {
    if (this.isBrowser) {
      try {
        localStorage.setItem(name, JSON.stringify(data));
        return true;
      } catch (e) { }
    }
    return null;
  }

  get(name: string) {
    if (this.isBrowser) {
      try {
        const data = JSON.parse(localStorage.getItem(name) || '{}');
        if (Object.keys(data).length !== 0) {
          return data;
        }
      } catch (e) { }
    }
    return null;
  }

  getProperty(parent: string, child: string | number) {
    if (this.isBrowser) {
      try {
        const data = JSON.parse(localStorage.getItem(parent) || '{}');
        if (Object.keys(data).length !== 0) {
          return data[child] || null;
        }
      } catch (e) { }
    }
    return null;
  }

  delete(name: string) {
    if (this.isBrowser) {
      try {
        localStorage.removeItem(name);
        return true;
      } catch (e) { }
    }
    return null;
  }

  check(name: string, data: any) {
    if (this.get(name)) {
      this.delete(name);
      this.add(name, data);
    } else {
      this.add(name, data);
    }
  }

  getFromSession(name: string) {
    if (this.isBrowser) {
      try {
        const data = JSON.parse(sessionStorage.getItem(name) || '{}');
        if (Object.keys(data).length !== 0) {
          return data;
        }
      } catch (e) { }
    }
    return null;
  }

  deleteFromSession(name: string) {
    if (this.isBrowser) {
      try {
        sessionStorage.removeItem(name);
        return true;
      } catch (e) { }
    }
    return null;
  }

  checkFromSession(name: string, data: any) {
    if (this.getFromSession(name)) {
      this.deleteFromSession(name);
      this.addSessionData(name, data);
    } else {
      this.addSessionData(name, data);
    }
  }

  addSessionData(name: string, data: any) {
    if (this.isBrowser) {
      try {
        sessionStorage.setItem(name, JSON.stringify(data));
        return true;
      } catch (e) { }
    }
    return null;
  }

  getPropertyFromSession(parent: string, child: string | number) {
    if (this.isBrowser) {
      try {
        const data = JSON.parse(sessionStorage.getItem(parent) || '{}');
        if (Object.keys(data).length !== 0) {
          return data[child] || null;
        }
      } catch (e) { }
    }
    return null;
  }

  getCookie(name: string) {
    if (this.isBrowser) {
      const value = '; ' + document.cookie;
      const parts = value.split('; ' + name + '=');
      if (parts.length === 2) {
        return parts.pop().split(';').shift();
      }
    }
    return null;
  }

  getDecodedCookies(name: string) {
    if (this.isBrowser) {
      const value = '; ' + document.cookie;
      const parts = value.split('; ' + name + '=');
      if (parts.length === 2) {
        return JSON.parse(atob(decodeURIComponent(parts.pop().split(';').shift() || '')));
      }
    }
    return null;
  }

  setCookie(cname: string, cvalue: string, exdays: number, domain?: string): void {
    if (this.isBrowser) {
      let expires = '';
      if (exdays) {
        const date = new Date();
        date.setTime(date.getTime() + (exdays * 24 * 60 * 60 * 1000));
        expires = `; expires=${date.toUTCString()}`;
      }
      const domainString = domain ? `; domain=${domain}` : '';
      document.cookie = `${cname}=${cvalue || ''}${expires}; path=/${domainString}`;
    }
  }

  deleteCookie(name: string, domain?: string): void {
    if (this.isBrowser) {
      document.cookie = name + `=; Expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/; domain=${domain || ''}`;
    }
  }
}
